import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { PlatformListToolbar } from "@/components/platform/PlatformListToolbar";
import { usePlatformList } from "@/components/platform/usePlatformList";
import { DataTableDateTime } from "@/components/data-table";
import { AdminAlert, AdminEmptyState, AdminPageHeader, AdminPill } from "@/components/shell/AdminPage";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  clearRememberedAccountId,
  stashActorSession,
  writeImpersonationMeta,
} from "@/lib/impersonation";
import { fetchPlatformUsers, impersonatePlatformUser, type PlatformUserRow } from "@/lib/platform-api";

const ONLINE_FILTER_OPTIONS = [
  { value: "", label: "全部" },
  { value: "true", label: "在线" },
  { value: "false", label: "离线" },
] as const;

const PLATFORM_ROLE_LABELS: Record<string, string> = {
  operator: "运营",
  admin: "管理员",
  superadmin: "超级管理员",
};

function formatPlatformRole(role: string | null) {
  if (!role) return "—";
  return PLATFORM_ROLE_LABELS[role] ?? role;
}

function userRowLabel(row: PlatformUserRow) {
  return row.display_name?.trim() || row.email || row.phone || `用户 #${row.id}`;
}

export default function PlatformUsersPage() {
  const { user, applySession } = useAuth();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<PlatformUserRow | null>(null);
  const canImpersonate = user?.platform_role === "superadmin";

  const fetcher = useCallback(async ({ q, filter, page }: { q: string; filter: string; page: number }) => {
    const result = await fetchPlatformUsers({
      q: q || undefined,
      online: (filter as "true" | "false" | "") || "",
      page,
    });
    return { rows: result.users, meta: result.meta };
  }, []);

  const { inputQ, setInputQ, filter, setFilter, page, setPage, rows, meta, loading, error, submitSearch } =
    usePlatformList<PlatformUserRow>({ fetcher });

  function requestImpersonate(row: PlatformUserRow) {
    if (!canImpersonate || !user) return;
    if (row.id === user.id) {
      toast.error("不能模拟登录自己");
      return;
    }
    setImpersonateTarget(row);
  }

  async function confirmImpersonate() {
    if (!impersonateTarget || !user) return;
    const row = impersonateTarget;
    setBusyId(row.id);
    try {
      const access = api.getAccessToken();
      const refresh = api.getRefreshToken();
      if (!access || !refresh) throw new Error("缺少当前会话");

      stashActorSession({
        access_token: access,
        refresh_token: refresh,
        expires_at: window.localStorage.getItem("c456.access_expires_at"),
      });

      const session = await impersonatePlatformUser(row.id);
      writeImpersonationMeta(session.impersonation);
      clearRememberedAccountId();
      setImpersonateTarget(null);
      applySession(session);
      toast.success(`已进入「${session.impersonation.target_label}」视角`);
      navigate("/", { replace: true });
    } catch (caughtError) {
      toast.error(caughtError instanceof ApiError ? caughtError.message : "模拟登录失败");
    } finally {
      setBusyId(null);
    }
  }

  const colSpan = canImpersonate ? 9 : 8;

  return (
    <div className="w-full space-y-6">
      <AdminPageHeader
        title="平台用户"
        description="查看与检索全平台注册用户、在线状态与平台角色。超级管理员可隐身模拟登录。"
      />
      <PlatformListToolbar
        searchPlaceholder="搜索邮箱、手机或昵称…"
        search={inputQ}
        onSearchChange={setInputQ}
        onSearchSubmit={submitSearch}
        filterValue={filter}
        onFilterChange={setFilter}
        filterOptions={[...ONLINE_FILTER_OPTIONS]}
        meta={meta}
        page={page}
        onPageChange={setPage}
        loading={loading}
      />
      {error ? <AdminAlert>{error}</AdminAlert> : null}
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">ID</TableHead>
              <TableHead>显示名</TableHead>
              <TableHead>邮箱</TableHead>
              <TableHead>手机</TableHead>
              <TableHead>平台角色</TableHead>
              <TableHead>在线</TableHead>
              <TableHead>最近活跃</TableHead>
              <TableHead>注册时间</TableHead>
              {canImpersonate ? <TableHead className="w-[120px]">操作</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="h-24 text-center text-muted-foreground">
                  加载中…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="p-0">
                  <AdminEmptyState>
                    <p className="font-medium text-foreground">暂无匹配用户</p>
                    <p className="mt-2">试试调整搜索词或在线筛选条件。</p>
                  </AdminEmptyState>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="tabular-nums text-muted-foreground">{row.id}</TableCell>
                  <TableCell>{row.display_name?.trim() || "—"}</TableCell>
                  <TableCell>{row.email || "—"}</TableCell>
                  <TableCell>{row.phone || "—"}</TableCell>
                  <TableCell>{formatPlatformRole(row.platform_role)}</TableCell>
                  <TableCell>
                    <AdminPill tone={row.online ? "success" : "muted"}>{row.online ? "在线" : "离线"}</AdminPill>
                  </TableCell>
                  <TableCell>
                    <DataTableDateTime value={row.last_activity_at} />
                  </TableCell>
                  <TableCell>
                    <DataTableDateTime value={row.created_at} />
                  </TableCell>
                  {canImpersonate ? (
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={row.id === user?.id || busyId === row.id}
                        onClick={() => requestImpersonate(row)}
                      >
                        {busyId === row.id ? "进入中…" : "模拟登录"}
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ConfirmDialog
        open={impersonateTarget != null}
        onOpenChange={(open) => {
          if (!open && busyId == null) setImpersonateTarget(null);
        }}
        title="隐身模拟登录"
        description={
          impersonateTarget ? (
            <>
              将以「{userRowLabel(impersonateTarget)}」的身份进入工作台查看其账户。
              <span className="mt-2 block">对方不会看到登录记录，也不会显示在线/最近活跃变化。</span>
            </>
          ) : (
            ""
          )
        }
        confirmLabel="开始模拟"
        confirming={busyId != null}
        onConfirm={() => void confirmImpersonate()}
      />
    </div>
  );
}
